import { get, isEqual, compact } from 'lodash';
import { fetchRceSettings } from '../actions/rce-settings';

import { PUB_SUB_CHANNEL_APP } from '@wix/communities-blog-client-common/dist/src/constants/pub-sub-channels';
import { getPostByIdOrSlug } from '../selectors/post-selectors';
import { isInPostEditor, isInCreatePostPage, isSameLocation } from './detect-route';
import { getPostEditorPostId, getPostEditorPost, getPostEditorDraftKey } from '../selectors/post-editor-selectors';
import fetchCategories from '../actions/fetch-categories';
import { fetchWriters } from '../actions/fetch-writers';
import { fetchPost } from '../actions/fetch-post';
import { setPostEditorPost, cleanPostEditorState, updatePostEditorPost } from '../actions/post-editor';
import { getCurrentLocation, isInitialLocation } from '../selectors/location-history-selectors';
import { getConfig } from './config';
import { isAnotherWriterEditingThePost } from '@wix/communities-blog-universal/dist/src/post-utils';
import { openModal } from '../actions/open-modal';
import { getCategories } from '../selectors/categories-selectors';
import {
  MODAL_TYPE_POST_EDITOR,
  MODAL_TYPE_POST_FORM_EDIT_IN_PROGRESS,
} from '@wix/communities-blog-client-common/dist/src/constants/modal-types';
import { getModal } from '../selectors/modal-selectors';
import { getIsMobile } from '@wix/communities-blog-client-common/dist/src/selectors/search-params-selectors';
import { closeModal } from '../actions/close-modal';
import { replace } from './router-utils';
import {
  getCurrentUserId,
  getCurrentUser,
} from '@wix/communities-blog-client-common/dist/src/selectors/current-user-selectors';
import createPermissionHelpers from './create-permission-helpers';
import createPermissionChecker from './create-permission-checker';

const fetchInitialData = (state, dispatch, postSlug) => {
  const { can } = createPermissionHelpers(createPermissionChecker(state, getCurrentUser(state)));
  const initialData = compact([
    dispatch(fetchRceSettings()),
    dispatch(fetchCategories()),
    can('change-owner', 'post') && dispatch(fetchWriters()),
    postSlug
      ? dispatch(fetchPost(postSlug)).then(post => dispatch(setPostEditorPost(post)))
      : dispatch(setPostEditorPost()),
  ]);

  return Promise.all(initialData);
};

export const logPostEditorAction = (...message) =>
  // eslint-disable-next-line no-console
  getConfig().logPostEditorActions && console.log(getConfig().bundleName, '[POST EDITOR]', ...message);

export const fetchPostEditorSSRData = ({ store, params: { postSlug } }) =>
  fetchInitialData(store.getState(), store.dispatch, postSlug);

const isEditingSamePost = ({ postSlugOrId, state }) => {
  const locationPostId = get(getPostByIdOrSlug(state, postSlugOrId), '_id');
  logPostEditorAction(`check if editing same post, slug: ${postSlugOrId} id: ${locationPostId}`);
  if (!locationPostId) {
    logPostEditorAction('post not found, fetching');
    return false;
  }
  const postEditorPostId = getPostEditorPostId(state);
  logPostEditorAction('current post id:', postEditorPostId);
  return postEditorPostId === locationPostId;
};

export const initializeMiddlewareAfterSSR = ({ state, dispatch }) => {
  const post = getPostEditorPost(state);
  if (post) {
    return dispatch(setPostEditorPost(post));
  }
};

const onSameLocation = ({ location }) => {
  logPostEditorAction('same location', location.pathname);
};

const isPostEditorPostFresh = state => {
  return isEqual(getPostEditorPost(state), {});
};

const onCreatePost = ({ state, dispatch, draftKey }) => {
  if (isPostEditorPostFresh(state)) {
    return logPostEditorAction('fresh post on create post - not refreshing');
  }

  logPostEditorAction('creating post');
  dispatch(cleanPostEditorState(draftKey));
  return fetchInitialData(state, dispatch);
};

const onEditingSamePost = ({ postSlugOrId }) => {
  logPostEditorAction('editing same post', postSlugOrId);
};

const onEditingAnotherPost = ({ state, dispatch, postSlugOrId, draftKey }) => {
  logPostEditorAction('post editing started', postSlugOrId);
  dispatch(cleanPostEditorState(draftKey));
  return fetchInitialData(state, dispatch, postSlugOrId);
};

const onPostEditorLeft = ({ dispatch, location, draftKey }) => {
  logPostEditorAction('post editor left', location.pathname);
  dispatch(cleanPostEditorState(draftKey));
};

const onEditPost = ({ state, dispatch, draftKey, postSlugOrId }) =>
  isEditingSamePost({ postSlugOrId, state })
    ? onEditingSamePost({ postSlugOrId })
    : onEditingAnotherPost({ state, dispatch, postSlugOrId, draftKey });

export const initPostEditor = postSlugOrId => (dispatch, getState) => {
  const state = getState();
  const draftKey = getPostEditorDraftKey(state);

  if (postSlugOrId) {
    return onEditPost({ state, dispatch, postSlugOrId, draftKey });
  }

  return onCreatePost({ state, dispatch, draftKey });
};

export const updatePostEditorOnNavigation = ({ state, dispatch, location }) => {
  if (!isInitialLocation(state) && isSameLocation(getCurrentLocation(state), location)) {
    return onSameLocation({ location });
  }

  const draftKey = getPostEditorDraftKey(state);
  if (isInCreatePostPage(location)) {
    return onCreatePost({ state, dispatch, draftKey });
  }

  if (isInPostEditor(location)) {
    if (isEditingSamePost({ state, location })) {
      return onEditingSamePost({ location });
    } else {
      return onEditingAnotherPost({ state, dispatch, draftKey });
    }
  }

  if (getPostEditorPost(state)) {
    return onPostEditorLeft({ dispatch, location, draftKey });
  }
};

export const updateEditedPostOnDraftSaved = ({ dispatch, previousPost, post }) => {
  logPostEditorAction('draft saved');
  dispatch(updatePostEditorPost(post));

  if (!previousPost._id) {
    const postEditPath = `/${post._id}/edit`;
    logPostEditorAction('updating post edit path', postEditPath);
    dispatch({
      ...replace(postEditPath),
      meta: { publish: [PUB_SUB_CHANNEL_APP] },
    });
  }
};

const onMobilePostEditorNotOpen = ({ state, dispatch }) => {
  logPostEditorAction('opening mobile modal');

  return dispatch(
    openModal({
      type: MODAL_TYPE_POST_EDITOR,
      props: {
        post: getPostEditorPost(state),
        categories: getCategories(state),
      },
    }),
  );
};

const isPostEditorModalOpen = state => getModal(state, MODAL_TYPE_POST_EDITOR);

export const openMobilePostEditorModal = ({ state, dispatch }) => {
  if (!getIsMobile(state)) {
    return;
  }

  if (!isPostEditorModalOpen(state)) {
    return onMobilePostEditorNotOpen({ state, dispatch });
  }
};

export const closePostEditorModalOnCancel = ({ dispatch }) => {
  logPostEditorAction('closing post editor modal on cancel');
  dispatch(closeModal({ type: MODAL_TYPE_POST_EDITOR, resolve: {} }));
};

export const closePostEditorModalOnPublish = ({ dispatch, post }) => {
  logPostEditorAction('closing post editor modal on publish');
  dispatch(closeModal({ type: MODAL_TYPE_POST_EDITOR, resolve: post }));
};

export const closePostEditorModalOnBrowserBack = ({ state, dispatch }) => {
  if (isPostEditorModalOpen(state)) {
    logPostEditorAction('closing post editor modal on back navigation');
    dispatch(closeModal({ type: MODAL_TYPE_POST_EDITOR }));
  }
};

export const openPostFormEditInProgressModalForLocked = ({ state, dispatch }) => {
  if (isAnotherWriterEditingThePost(getPostEditorPost(state), getCurrentUserId(state))) {
    dispatch(openModal({ type: MODAL_TYPE_POST_FORM_EDIT_IN_PROGRESS }));
  }
};
